/**
 * Row-scoped mutations for `clients` and `leads`.
 *
 * WHY THIS FILE EXISTS
 *
 * `ClientList.tsx` issued six filter-bearing Supabase mutations inline, each one
 * carrying its own `.eq("id", …)`:
 *
 *     clients .update(payload) .eq("id", editingClient.id)
 *     clients .delete()        .eq("id", id)               ← the dangerous one
 *     clients .update({status}).eq("id", client.id)
 *     leads   .delete()        .eq("id", leadId)
 *     leads   .update({status}).eq("id", leadId)   × 2
 *
 * All six had ZERO behavioural coverage. Dropping a single `.eq` turns a
 * one-row statement into a whole-table one, and **every test in the repo stays
 * green** — five independent mutations were tried against that blind spot and
 * all five survived.
 *
 * `clients.delete()` is the worst of them. Unscoped it deletes every client row,
 * and migration 060's `client_stage_history` is append-only with
 * `ON DELETE CASCADE` — the ledger is unreconstructable, so that mistake is not
 * recoverable from a backup of the row that caused it.
 *
 * THE CHOICE MADE HERE, AND WHY
 *
 * The filter lives INSIDE these helpers. A caller cannot forget it, because a
 * caller cannot express it. That is deliberately a design fix rather than a test
 * fix: prefer a shape that cannot express the bug over a test that reports it.
 * The alternative — mounting a 3,570-line component against this repo's known
 * landmines (`resetMocks` wiping factory mocks, a silent jest hang meaning a
 * render loop) — builds the kind of harness a future seat routes around, and a
 * check people learn to bypass is gone within a month.
 *
 * WHAT THIS DOES NOT PROTECT — a named gap, not a footnote
 *
 * These helpers guarantee a mutation is SCOPED. They cannot guarantee it is
 * scoped to the RIGHT row. `updateClientById(db, someOtherClient.id, patch)` is
 * well-formed and wrong, and neither the tests here nor the source contract in
 * `__tests__/clientMutations.test.ts` can see it. Catching that requires driving
 * the mounted component and asserting which id reaches the wire. That cost was
 * weighed and declined for now; it is recorded so nobody mistakes this file for
 * more than it is.
 */

/**
 * The narrow slice of the Supabase client these helpers need.
 *
 * Declared structurally rather than importing `SupabaseClient` so the tests can
 * pass a recording stub without fighting the full generated type — and so this
 * module states its own requirement instead of inheriting a 200-method surface.
 */
export interface ScopedMutationResult {
  error: unknown;
}

export interface ScopedMutationDb {
  from(table: string): {
    update(values: Record<string, unknown>): {
      eq(column: string, value: string): PromiseLike<ScopedMutationResult>;
    };
    delete(): {
      eq(column: string, value: string): PromiseLike<ScopedMutationResult>;
    };
  };
}

/** Tables these helpers are allowed to touch. */
type ScopedTable = "clients" | "leads";

/**
 * Reject an id that cannot scope a statement, BEFORE issuing it.
 *
 * An `undefined` or empty id reaching `.eq("id", undefined)` is the same class of
 * accident as omitting `.eq` — the filter is present but vacuous, and what
 * PostgREST does with it is not something to find out on production data. Fail
 * closed and loudly instead.
 */
function assertScopingId(id: unknown, operation: string): string {
  if (typeof id !== "string" || !id.trim()) {
    throw new Error(`${operation}: refusing an unscoped mutation — id is required`);
  }
  return id;
}

/** UPDATE exactly one row of `table`, by id. */
function updateById(
  db: ScopedMutationDb,
  table: ScopedTable,
  id: string,
  values: Record<string, unknown>
): PromiseLike<ScopedMutationResult> {
  const scopedId = assertScopingId(id, `update ${table}`);
  return db.from(table).update(values).eq("id", scopedId);
}

/** DELETE exactly one row of `table`, by id. */
function deleteById(
  db: ScopedMutationDb,
  table: ScopedTable,
  id: string
): PromiseLike<ScopedMutationResult> {
  const scopedId = assertScopingId(id, `delete ${table}`);
  return db.from(table).delete().eq("id", scopedId);
}

/** UPDATE one client row. Covers ClientList's edit-save and status-change paths. */
export function updateClientById(
  db: ScopedMutationDb,
  id: string,
  values: Record<string, unknown>
): PromiseLike<ScopedMutationResult> {
  return updateById(db, "clients", id, values);
}

/**
 * DELETE one client row.
 *
 * The single most dangerous statement in this component: unscoped it cascades
 * migration 060's append-only `client_stage_history`, which cannot be rebuilt.
 */
export function deleteClientById(
  db: ScopedMutationDb,
  id: string
): PromiseLike<ScopedMutationResult> {
  return deleteById(db, "clients", id);
}

/** UPDATE one lead row. Covers the archive and restore paths. */
export function updateLeadById(
  db: ScopedMutationDb,
  id: string,
  values: Record<string, unknown>
): PromiseLike<ScopedMutationResult> {
  return updateById(db, "leads", id, values);
}

/** DELETE one lead row. */
export function deleteLeadById(
  db: ScopedMutationDb,
  id: string
): PromiseLike<ScopedMutationResult> {
  return deleteById(db, "leads", id);
}

// ---------------------------------------------------------------------------
// STATUS CHANGE — one writer, because a business event must not depend on
// which screen fired it.
//
// 🔴 The defect this replaces: `ClientList.handleStatusChange` wrote THREE
// records (the client UPDATE, an `activities` row, a `notifications` row)
// while the Messages rail's own `moveToStatus` wrote only the UPDATE. Same
// move, same client, different history — the timeline lost the entry and the
// team lost the notification whenever the operator happened to be in the
// thread instead of the list. Found by an independent reviewer reading both
// surfaces side by side.
//
// Migration 060's stage-history trigger fires on the UPDATE either way, so the
// stage ledger was never at risk. What differed is everything a human reads.
//
// Note on migration 031: `status_changed` is deliberately NOT one of that
// trigger's contact types (call_logged, sent_text, guest_card_sent, …), so
// writing this activity does NOT bump `clients.last_contacted_at`. Moving a
// stage is not contacting someone. Verified against
// docs/database/migrations/031_activity_last_contacted_trigger.sql:50-72.
// ---------------------------------------------------------------------------

export interface StatusChangeContext {
  clientId: string;
  clientName: string;
  previousStatus: string;
  nextStatus: string;
  /** Label lookup so callers keep ONE source of stage labels. */
  labelFor: (status: string) => string;
  actorUserId: string | null;
}

export interface StatusChangeOutcome {
  /** Set when the client UPDATE failed — the move did NOT happen. */
  error: unknown | null;
  /** True when the audit rows were written. False means the move DID happen
   * but its trail is incomplete — callers should log, not fail. */
  sideEffectsWritten: boolean;
  /**
   * The errors behind a `sideEffectsWritten: false`, in the order the writes
   * were issued. Empty when everything landed.
   *
   * A bare boolean is why `ClientList.tsx` could only log a bare string: the
   * writer HELD the RLS code and the PostgREST message and discarded them at
   * the return statement, so the one place that wanted to report the failure
   * had nothing to report. Carrying them costs nothing and is the difference
   * between "something failed" and "42501 on notifications".
   */
  sideEffectErrors: unknown[];
}

/**
 * Move a client to a new status and write the records a human later reads.
 *
 * Failure semantics are deliberately asymmetric:
 *  · the UPDATE failing means the move did not happen -> `error` is returned
 *    and the caller must NOT show the new stage.
 *  · the audit rows failing means the move DID happen -> we never roll it
 *    back or claim it failed; `sideEffectsWritten: false` lets the caller
 *    report an incomplete trail without lying about the stage.
 */
export async function changeClientStatus(
  db: any,
  ctx: StatusChangeContext
): Promise<StatusChangeOutcome> {
  const { error } = await updateClientById(db, ctx.clientId, {
    status: ctx.nextStatus,
  });
  if (error) return { error, sideEffectsWritten: false, sideEffectErrors: [] };

  try {
    const results = await Promise.all([
      db.from("activities").insert({
        user_id: ctx.actorUserId,
        activity_type: "status_changed",
        entity_type: "client",
        entity_id: ctx.clientId,
        metadata: {
          client_name: ctx.clientName,
          old_status: ctx.previousStatus,
          new_status: ctx.nextStatus,
        },
      }),
      db.from("notifications").insert({
        type: "status_change",
        visibility: "all",
        title: `${ctx.clientName} moved to ${ctx.labelFor(ctx.nextStatus)}`,
        message: `Status changed from ${ctx.labelFor(ctx.previousStatus)}`,
        entity_type: "client",
        entity_id: ctx.clientId,
        triggered_by: ctx.actorUserId,
      }),
    ]);

    // 🔴 THE ERROR ARRIVES AS A VALUE, NOT AS A THROW.
    //
    // supabase-js v2 `.insert()` RESOLVES with `{ data, error }` for a
    // PostgREST error and REJECTS only on transport failure. There is no
    // `.throwOnError()` anywhere in `frontend/src` — zero hits, checked. So the
    // `try/catch` alone could NEVER see an RLS denial, an FK violation or a
    // check violation: `Promise.all` resolved, and this returned
    // `sideEffectsWritten: true` with nothing written.
    //
    // That made the "timeline entry could not be written" notice DEAD CODE, and
    // it was live rather than theoretical: migration
    // `002_fix_rls_policies.sql:131-137` restricts notifications inserts to
    // admins (`WITH CHECK profiles.role = 'admin'`), so every non-admin agent
    // moving a stage got a silent 42501 and saw clean success.
    //
    // Checking `error` is the same pattern this function already uses for the
    // UPDATE seventeen lines up. The catch stays for genuine transport failures
    // — both shapes are real, and only one of them was handled.
    //
    // A result that is not shaped `{ error }` cannot be confirmed as written,
    // so it counts as NOT written. Same principle as the backend's consent
    // write gate: fail toward the least assertion, because claiming an audit
    // trail exists when it does not is the failure this whole PR is about.
    const unwritten = results.filter(
      (r: any) => !r || typeof r !== "object" || !("error" in r) || r.error
    );
    if (unwritten.length > 0) {
      const sideEffectErrors = unwritten.map(
        (r: any) => r?.error ?? new Error("malformed insert result")
      );
      console.error(
        "changeClientStatus: stage moved but audit writes failed",
        sideEffectErrors
      );
      return { error: null, sideEffectsWritten: false, sideEffectErrors };
    }
    return { error: null, sideEffectsWritten: true, sideEffectErrors: [] };
  } catch (err) {
    // Transport failure — the network never reached PostgREST.
    console.error("changeClientStatus: audit writes threw", err);
    return { error: null, sideEffectsWritten: false, sideEffectErrors: [err] };
  }
}
